import { useEffect, useState } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { noop } from 'lodash';

import { TIME_IN_MS } from 'Utilities/dateHelpers/dateHelpers';
import { generateTimeRemainingText } from 'Utilities/generateTimeRemainingText';

interface CountdownTimerResult {
	shouldShowCountdown: boolean;
	timeRemainingText: string;
}

const calculateNextUpdateInterval = (expiryDate: string = ''): number | undefined => {
	const now = dayjs();
	const expiry = dayjs(expiryDate);
	const diffInMilliseconds = expiry.diff(now);
	const getTimeTillNearestHourBeforeExpiry = (hours: number) => expiry.subtract(hours, 'hours').diff(now);

	if (diffInMilliseconds > TIME_IN_MS.hour * 3) {
		return getTimeTillNearestHourBeforeExpiry(3); // Time until the expiry date is exactly 3 hours away
	}
	if (diffInMilliseconds > TIME_IN_MS.hour * 2) {
		return getTimeTillNearestHourBeforeExpiry(2); // Time until the expiry date is exactly 2 hours away
	}
	if (diffInMilliseconds > TIME_IN_MS.hour) {
		return getTimeTillNearestHourBeforeExpiry(1); // Time until the expiry date is exactly 1 hour away
	}
	if (diffInMilliseconds > TIME_IN_MS.minute) {
		return now.endOf('minute').diff(now); // Time until the next minute
	}
	if (diffInMilliseconds > 0) {
		return now.endOf('second').diff(now); // Time until the next second
	}
	return undefined; // Expiry date has passed or is invalid
};

export const useCountdownTimer = (expiryDate: string = ''): CountdownTimerResult => {
	const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
	const vehicleExpiryDate = dayjs(expiryDate);
	const shouldShowCountdown = vehicleExpiryDate.isBetween(currentDate, currentDate.add(3, 'hours'));
	const timeRemainingText = generateTimeRemainingText(vehicleExpiryDate);

	useEffect(() => {
		const updateCurrentDate = () => setCurrentDate(dayjs());
		const interval = calculateNextUpdateInterval(expiryDate);

		if (!interval) {
			return noop;
		}

		const timeoutId = setTimeout(updateCurrentDate, interval);

		return () => clearTimeout(timeoutId);
	}, [currentDate, expiryDate]);

	return { shouldShowCountdown, timeRemainingText };
};
